import CreditCardIcon from "@mui/icons-material/CreditCard";
import LogoutIcon from "@mui/icons-material/Logout";
import SettingsIcon from "@mui/icons-material/Settings";
import TableChartIcon from "@mui/icons-material/TableChart";
import {
  Avatar,
  Box,
  Divider,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Popover,
  Typography,
} from "@mui/material";
import NextLink from "next/link";
import { useRouter } from "next/router";
import PropTypes from "prop-types";
import type { FC } from "react";
import toast from "react-hot-toast";
import { selectUserRole, selectCurrentPlan } from "src/slices/system/selectors";
import { useSelector } from "src/store";
import { useAuth } from "../../hooks/use-auth";
import { UserCircle as UserCircleIcon } from "../../icons/user-circle";

interface AccountPopoverProps {
  anchorEl: null | Element;
  onClose?: () => void;
  open?: boolean;
}

export const AccountPopover: FC<AccountPopoverProps> = (props) => {
  const { anchorEl, onClose, open, ...other } = props;
  const router = useRouter();
  const userRole = useSelector(selectUserRole);
  const currentPlan = useSelector(selectCurrentPlan);
  const { logout, user } = useAuth();

  const isSpreadsheetAvailable = currentPlan?.spreadsheet === "true";

  const handleLogout = async (): Promise<void> => {
    try {
      onClose?.();
      await logout();
      router.push("/authentication/login/");
    } catch (err) {
      toast.error("Unable to logout.");
    }
  };

  return (
    <Popover
      anchorEl={anchorEl}
      anchorOrigin={{
        horizontal: "center",
        vertical: "bottom",
      }}
      keepMounted
      onClose={onClose}
      open={open}
      PaperProps={{ sx: { width: 300 } }}
      transitionDuration={0}
      {...other}
    >
      <Box
        sx={{
          alignItems: "center",
          p: 2,
          display: "flex",
        }}
      >
        <Avatar
          src={user.avatar}
          sx={{
            height: 40,
            width: 40,
          }}
        >
          <UserCircleIcon fontSize="small" />
        </Avatar>
        <Box
          sx={{
            ml: 1,
          }}
        >
          <Typography variant="body1">{user.email}</Typography>
        </Box>
      </Box>
      <Divider />
      <Box sx={{ my: 1 }}>
        <NextLink href="/dashboard/account/" passHref>
          <MenuItem component="a">
            <ListItemIcon>
              <SettingsIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={<Typography variant="body1">Settings</Typography>}
            />
          </MenuItem>
        </NextLink>
        {(userRole == "owner" || userRole == "full") && (
          <NextLink href="/dashboard/pricing/" passHref>
            <MenuItem component="a">
              <ListItemIcon>
                <CreditCardIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={<Typography variant="body1">Pricing</Typography>}
              />
            </MenuItem>
          </NextLink>
        )}
        {isSpreadsheetAvailable && (
          <>
            <Divider />
            <NextLink href="/dashboard/bulk-briefs" passHref>
              <MenuItem component="a">
                <ListItemIcon>
                  <TableChartIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={<Typography variant="body1">Bulk Briefs</Typography>}
                />
              </MenuItem>
            </NextLink>
          </>
        )}
        {user?.role == "admin" && (
          <>
            <Divider />
            <NextLink href="/dashboard/management" passHref>
              <MenuItem component="a">
                <ListItemIcon>
                  <CreditCardIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={<Typography variant="body1">Management</Typography>}
                />
              </MenuItem>
            </NextLink>
          </>
        )}
        <Divider />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={<Typography variant="body1">Logout</Typography>}
          />
        </MenuItem>
      </Box>
    </Popover>
  );
};

AccountPopover.propTypes = {
  anchorEl: PropTypes.any,
  onClose: PropTypes.func,
  open: PropTypes.bool,
};
